import React, { useState, useEffect } from 'react';
import { User, GraduationCap, FileText, Mail, Phone, Globe, AlertCircle, CheckCircle } from 'lucide-react';

const EtudiantSupemirView = () => {
  const [etudiant, setEtudiant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getEtudiantId = () => {
    const pathParts = window.location.pathname.split('/');
    const etudiantIndex = pathParts.indexOf('etudiant-supemir');
    if (etudiantIndex !== -1 && pathParts[etudiantIndex + 1]) {
      return pathParts[etudiantIndex + 1];
    }
    return new URLSearchParams(window.location.search).get('id') || 'demo';
  };

  const etudiantId = getEtudiantId();

  useEffect(() => {
    fetchEtudiantInfo();
  }, []);

  const fetchEtudiantInfo = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`     https://vmi1977988.contaboserver.net/api2/etudiant-supemir/${etudiantId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Étudiant non trouvé');
        }
        throw new Error('Erreur lors du chargement des informations');
      }

      const data = await response.json();

      const etudiantAvecImage = {
        ...data,
        image: data.image && data.image.trim() !== ''
          ? `     https://vmi1977988.contaboserver.net${data.image}`
          : ''
      };

      setEtudiant(etudiantAvecImage);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des informations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getFormationLabel = (formation) => {
    const labels = {
      'CYCLE_INGENIEUR': 'Cycle Ingénieur',
      'LICENCE_PRO': 'Licence Professionnelle',
      'MASTER_PRO': 'Master Professionnel',
      'MASI': 'MASI',
      'IRM': 'IRM'
    };
    return labels[formation] || formation;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Chargement des informations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <AlertCircle size={64} color="#ef4444" />
          <h2 style={styles.errorTitle}>Erreur</h2>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  if (!etudiant) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <User size={64} color="#94a3b8" />
          <h2 style={styles.errorTitle}>Étudiant non trouvé</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.mainTitle}>🎓 SUPEMIR</h1>
          <p style={styles.subtitle}>Informations Étudiant</p>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.photoSection}>
              {etudiant.image ? (
                <img src={etudiant.image} alt={etudiant.nomComplet} style={styles.photo} />
              ) : (
                <div style={styles.photoPlaceholder}>
                  <User size={64} color="#0066cc" strokeWidth={1.5} />
                </div>
              )}
            </div>
            <div style={styles.nameSection}>
              <h2 style={styles.name}>{etudiant.nomComplet}</h2>
              <div style={styles.badgeContainer}>
                <span style={styles.badge}>{etudiant.genre}</span>
                <span style={styles.badge}>{etudiant.pays}</span>
              </div>
            </div>
            <div style={styles.statusBadge}>
              <CheckCircle size={20} color="#10b981" />
              <span style={styles.statusText}>Vérifié</span>
            </div>
          </div>

          <div style={styles.divider}></div>

          <div style={styles.infoGrid}>
            {/* Formation */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <GraduationCap size={20} color="#0066cc" />
                Formation Académique
              </h3>
              <div style={styles.infoRows}>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Type de formation:</span>
                  <span style={styles.value}>{getFormationLabel(etudiant.formation)}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Niveau:</span>
                  <span style={styles.valueHighlight}>Niveau {etudiant.niveau}</span>
                </div>
                {etudiant.cycle && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Cycle:</span>
                    <span style={styles.value}>{etudiant.cycle}</span>
                  </div>
                )}
                {etudiant.specialite && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Spécialité:</span>
                    <span style={styles.value}>{etudiant.specialite}</span>
                  </div>
                )}
                {etudiant.option && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Option:</span>
                    <span style={styles.value}>{etudiant.option}</span>
                  </div>
                )}
                <div style={styles.infoRow}>
                  <span style={styles.label}>Année scolaire:</span>
                  <span style={styles.value}>{etudiant.anneeScolaire}</span>
                </div>
              </div>
            </div>

            {/* Identité */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FileText size={20} color="#0066cc" />
                Informations Personnelles
              </h3>
              <div style={styles.infoRows}>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Date de naissance:</span>
                  <span style={styles.value}>{formatDate(etudiant.dateNaissance)}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Lieu de naissance:</span>
                  <span style={styles.value}>{etudiant.lieuNaissance || 'N/A'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Pays:</span>
                  <span style={styles.value}>
                    <Globe size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    {etudiant.pays}
                  </span>
                </div>

                {etudiant.pays === 'Maroc' && etudiant.cin && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>CIN:</span>
                    <span style={styles.valueCode}>{etudiant.cin}</span>
                  </div>
                )}

                {etudiant.pays !== 'Maroc' && etudiant.passeport && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Passeport:</span>
                    <span style={styles.valueCode}>{etudiant.passeport}</span>
                  </div>
                )}

                {etudiant.codeMassar && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Code Massar:</span>
                    <span style={styles.valueCode}>{etudiant.codeMassar}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <Mail size={20} color="#0066cc" />
                Coordonnées
              </h3>
              <div style={styles.infoRows}>
                {etudiant.email && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Email:</span>
                    <a href={`mailto:${etudiant.email}`} style={styles.link}>
                      {etudiant.email}
                    </a>
                  </div>
                )}
                {etudiant.telephone && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Téléphone étudiant:</span>
                    <a href={`tel:${etudiant.telephone}`} style={styles.link}>
                      <Phone size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {etudiant.telephone}
                    </a>
                  </div>
                )}
                {etudiant.telephoneResponsable && (
                  <div style={styles.infoRow}>
                    <span style={styles.label}>Téléphone responsable:</span>
                    <a href={`tel:${etudiant.telephoneResponsable}`} style={styles.link}>
                      <Phone size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {etudiant.telephoneResponsable}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              Document vérifié et authentique - SUPEMIR {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  header: {
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 50%, #003366 100%)',
    padding: '40px 24px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    textAlign: 'center',
  },
  mainTitle: {
    fontSize: '42px',
    fontWeight: '800',
    color: '#ffffff',
    margin: '0 0 8px 0',
    letterSpacing: '2px',
  },
  subtitle: {
    fontSize: '18px',
    color: '#ffffff',
    opacity: 0.95,
    margin: 0,
    fontWeight: '500',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 24px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    flexWrap: 'wrap',
    position: 'relative',
  },
  photoSection: {
    flexShrink: 0,
  },
  photo: {
    width: '150px',
    height: '150px',
    borderRadius: '16px',
    objectFit: 'cover',
    border: '4px solid #0066cc',
    boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)',
  },
  photoPlaceholder: {
    width: '150px',
    height: '150px',
    borderRadius: '16px',
    backgroundColor: '#e0f2fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '4px solid #0066cc',
  },
  nameSection: {
    flex: 1,
    minWidth: '250px',
  },
  name: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 12px 0',
  },
  badgeContainer: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  badge: {
    padding: '6px 16px',
    backgroundColor: '#e0f2fe',
    color: '#0066cc',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#dcfce7',
    borderRadius: '12px',
    border: '2px solid #10b981',
  },
  statusText: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#10b981',
  },
  divider: {
    height: '2px',
    backgroundColor: '#e5e7eb',
    margin: '0 40px',
  },
  infoGrid: {
    padding: '40px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '32px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: 0,
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb',
  },
  infoRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    flex: '0 0 auto',
    marginRight: '16px',
  },
  value: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#1e293b',
    textAlign: 'right',
    flex: '1',
  },
  valueHighlight: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0066cc',
    textAlign: 'right',
    flex: '1',
  },
  valueCode: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1e293b',
    fontFamily: "'Courier New', monospace",
    letterSpacing: '1px',
    textAlign: 'right',
    flex: '1',
  },
  link: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#0066cc',
    textDecoration: 'none',
    textAlign: 'right',
    flex: '1',
  },
  footer: {
    padding: '24px 40px',
    backgroundColor: '#f8fafc',
    borderTop: '2px solid #e5e7eb',
    textAlign: 'center',
  },
  footerText: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
    fontWeight: '500',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '20px',
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '5px solid #e2e8f0',
    borderTop: '5px solid #0066cc',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '18px',
    color: '#64748b',
    fontWeight: '600',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '20px',
    padding: '40px',
  },
  errorTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  errorText: {
    fontSize: '16px',
    color: '#64748b',
    textAlign: 'center',
    maxWidth: '500px',
  },
};

export default EtudiantSupemirView;